// Apolo IDE — ComposerOverflowMenu (Sprint chat-readability E3-T02).
//
// A compact overflow trigger that reveals lower-priority controls in a small
// popover. Compaction ≠ removal: every tool stays exactly ONE click away once
// the menu is open (the doctrine's hard law). Mirrors PanelGroupOverflow's
// proven, dependency-free popover behavior (click-outside + Esc close, focus
// returns to the trigger) instead of pulling in Radix/portals — no
// stacking-context surprises inside the IDE shell.
//
// It holds arbitrary children (the real MicButton/HandsFreeButton/Atalhos JSX),
// so the underlying features keep their own state/handlers untouched — we only
// relocate WHERE they render, never WHAT they do.
//
// (Pedro 2026-06-25, sprint cursor-strip-4 round 2) — generalized so the git
// bar can reuse it: `icon` swaps the trigger glyph (default MoreHorizontal;
// the git overflow passes Plus), `align` flips the popover anchor, and
// `triggerClassName` lets a caller match its own button shape (the git bar
// uses an h-7 pill instead of the 6×6 square).

import { useEffect, useId, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

const DEFAULT_TRIGGER =
  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing';

export default function ComposerOverflowMenu({
  children,
  label = 'Mais ações',
  icon: Icon = MoreHorizontal,
  align = 'right',
  triggerClassName = null,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (popRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Default trigger reflects open state with surface3 fill; a custom
  // triggerClassName is used verbatim (caller owns its look).
  const triggerClasses =
    triggerClassName ??
    DEFAULT_TRIGGER +
      ' ' +
      (open
        ? 'border-apolo-borderStrong bg-apolo-surface3 text-apolo-textPrimary'
        : 'border-apolo-borderSubtle bg-apolo-surface2/40 text-apolo-textSecondary hover:border-apolo-borderStrong hover:bg-apolo-surface2 hover:text-apolo-textPrimary');

  const popAnchor =
    align === 'left'
      ? 'left-0 origin-bottom-left'
      : 'right-0 origin-bottom-right';

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title={label}
        className={triggerClasses}
      >
        <Icon size={14} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={popRef}
          id={menuId}
          role="menu"
          aria-label={label}
          tabIndex={-1}
          className={
            'absolute bottom-[calc(100%+6px)] z-50 flex min-w-[200px] flex-col gap-1 rounded-lg border border-apolo-borderStrong bg-apolo-surface2 p-1.5 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.6)] ' +
            popAnchor
          }
        >
          {children}
        </div>
      )}
    </div>
  );
}
